import { platform } from '@/services/platform'
import type {
  AuthSession,
  AuthTokens,
  AuthUser,
  IAuthService,
  PasswordResetData,
  RegisterData,
} from './types'

const STORAGE_KEY = 'edge_mock_auth'
const PENDING_KEY = `${STORAGE_KEY}_pending`

/** Create a fake JWT for development. Not cryptographically valid. */
function createFakeJwt(payload: Record<string, unknown>): string {
  const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }))
  const body = btoa(JSON.stringify({ ...payload, exp: Math.floor(Date.now() / 1000) + 3600 }))
  return `${header}.${body}.mock-signature`
}

interface StoredAuth {
  user: AuthUser
  tokens: AuthTokens
}

/**
 * Mock auth service for local development.
 * Stores session in localStorage. Login always succeeds.
 */
export class MockAuthService implements IAuthService {
  private session: AuthSession | null = null
  private refreshInFlight: Promise<AuthSession | null> | null = null

  constructor() {
    this.restoreSession()
  }

  private restoreSession(): void {
    try {
      const raw = platform.secureStore.get(STORAGE_KEY)
      if (raw) {
        const stored: StoredAuth = JSON.parse(raw)
        this.session = { tokens: stored.tokens, user: stored.user }
      }
    } catch {
      platform.secureStore.remove(STORAGE_KEY)
    }
  }

  private persistSession(session: AuthSession): void {
    platform.secureStore.set(
      STORAGE_KEY,
      JSON.stringify({ tokens: session.tokens, user: session.user }),
    )
  }

  private createSession(email: string, givenName = 'Dev', familyName = 'User'): AuthSession {
    const sub = `mock-${btoa(email).replace(/=/g, '')}`
    const user: AuthUser = {
      email,
      sub,
      edgeUserId: sub,
      givenName,
      familyName,
      betaAccess: true,
    }
    const tokens: AuthTokens = {
      accessToken: createFakeJwt({ sub, email, token_use: 'access' }),
      idToken: createFakeJwt({
        sub,
        email,
        token_use: 'id',
        given_name: givenName,
        family_name: familyName,
      }),
      refreshToken: `mock-refresh-${sub}`,
    }
    return { tokens, user }
  }

  // --- Registration ---

  async register(data: RegisterData): Promise<void> {
    platform.secureStore.set(
      PENDING_KEY,
      JSON.stringify({ email: data.email, givenName: data.givenName, familyName: data.familyName }),
    )
  }

  async confirmRegistration(_email: string, _code: string): Promise<void> {
    // No-op in mock — always succeeds
  }

  async resendVerificationCode(_email: string): Promise<void> {
    // No-op in mock
  }

  // --- Login / Logout ---

  async login(email: string, _password: string): Promise<AuthSession> {
    let givenName = 'Dev'
    let familyName = 'User'
    try {
      const pending = platform.secureStore.get(PENDING_KEY)
      if (pending) {
        const data = JSON.parse(pending)
        if (data.email === email) {
          givenName = data.givenName ?? givenName
          familyName = data.familyName ?? familyName
          platform.secureStore.remove(PENDING_KEY)
        }
      }
    } catch {
      // ignore
    }

    const session = this.createSession(email, givenName, familyName)
    this.session = session
    this.persistSession(session)
    return session
  }

  async logout(): Promise<void> {
    this.session = null
    platform.secureStore.remove(STORAGE_KEY)
  }

  // --- Session ---

  async getSession(): Promise<AuthSession | null> {
    return this.session
  }

  async refreshSession(): Promise<AuthSession | null> {
    if (this.refreshInFlight) return this.refreshInFlight
    this.refreshInFlight = (async () => {
      if (!this.session) return null
      const refreshed = this.createSession(
        this.session.user.email,
        this.session.user.givenName,
        this.session.user.familyName,
      )
      this.session = refreshed
      this.persistSession(refreshed)
      return refreshed
    })().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  // --- Password reset ---

  async sendPasswordResetCode(_email: string): Promise<void> {
    // No-op in mock
  }

  async submitPasswordReset(_data: PasswordResetData): Promise<void> {
    // No-op in mock
  }

  // --- Profile ---

  async changePassword(_oldPassword: string, _newPassword: string): Promise<void> {
    if (!this.session) throw new Error('Not authenticated')
    // No-op in mock
  }

  async updateAttributes(attributes: Record<string, string>): Promise<void> {
    if (!this.session) throw new Error('Not authenticated')
    // Apply known attributes to mock user
    if (attributes['given_name']) {
      this.session.user.givenName = attributes['given_name']
    }
    if (attributes['family_name']) {
      this.session.user.familyName = attributes['family_name']
    }
    this.persistSession(this.session)
  }
}
